import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pageSEO } from '../utils/seo';

const PaymentFailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const seo = pageSEO.paymentFail;

  const errorCode = searchParams.get('code');
  const errorMessage = searchParams.get('message');

  const handleRetry = () => {
    navigate('/checkout');
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('payment.failTitle')}</h1>
          <p className="text-gray-600">{t('payment.failMessage')}</p>
        </div>

        {/* Error Details */}
        {(errorCode || errorMessage) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            {errorMessage && (
              <p className="text-sm text-red-800 mb-1">
                <span className="font-semibold">{t('payment.reason')}:</span> {errorMessage}
              </p>
            )}
            {errorCode && (
              <p className="text-xs text-red-600">
                <span className="font-semibold">{t('payment.errorCode')}:</span> {errorCode}
              </p>
            )}
          </div>
        )}

        {/* Common Reasons */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-2">{t('payment.commonReasons')}</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• {t('payment.limitExceeded')}</li>
            <li>• {t('payment.cardInfoError')}</li>
            <li>• {t('payment.passwordError')}</li>
            <li>• {t('payment.userCancelled')}</li>
            <li>• {t('payment.timeout')}</li>
          </ul>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleRetry}
            className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors font-semibold"
          >
            {t('payment.retry')}
          </button>
          <button
            onClick={handleGoHome}
            className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors"
          >
            {t('common.home')}
          </button>
        </div>

        {/* Contact Info */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>{t('payment.contactSupport')}</p>
          <p className="font-semibold mt-1">1588-0000</p>
        </div>
        </div>
      </div>
    </>
  );
};

export default PaymentFailPage;
